import { useForm } from '@mantine/form';
import { randomId } from '@mantine/hooks';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import FormWrapper from 'components/forms/FormWrapper';
import { ColumnPairing, ChastiaConfig } from 'components/forms/remote-source/config/types';
import ISObjects from 'components/forms/remote-source/config/chastia-config-form/is-objects/ISObjects';
import DeviceTypes from 'components/forms/remote-source/config/chastia-config-form/device-types/DeviceTypes';
import SpecialFields from 'components/forms/remote-source/config/chastia-config-form/special-fields/SpecialFields';
import Technicians from 'components/forms/remote-source/config/chastia-config-form/technicians/Technicians';
import useLoadingAction from 'hooks/use-loading-action';
import { FormProps } from 'components/forms/FormProps';
import { noop } from 'lodash';

import Button from 'components/Button';
import { useCallback } from 'react';
import { useCopyRemoteSourceConfig } from 'components/modals/copy-remote-source-config/CopyRemoteSourceConfigProvider';
import { showNotification } from '@mantine/notifications';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import WorkflowSettings from 'components/forms/remote-source/config/chastia-config-form/WorkflowSettings';

interface ChastiaConfigFormProps extends FormProps<ChastiaConfig> {
  remoteSourceId?: number;
}

/**
 * Applies defaults required by the Chastia configuration form.
 */
export function normalizeChastiaConfig(config: ChastiaConfig): ChastiaConfig {
  return {
    ...config,
    objects: config.objects ?? [{ key: randomId() }],
    types: config.types ?? [{ key: randomId(), columns: [] as ColumnPairing[] }],
    technicians: config.technicians ?? [{ key: randomId() }],
    specialFields: config.specialFields ?? [{ key: randomId() }],
    startRevisionsInProgress: config.startRevisionsInProgress === true,
    documentDeliveryMode: config.documentDeliveryMode ?? 'unsigned',
  };
}

/**
 * Chastia configuration form.
 */
export default function ChastiaConfigForm({
  initialValues = {
    objects: [{ key: randomId() }],
    types: [{ key: randomId(), columns: [] as ColumnPairing[] }],
    technicians: [{ key: randomId() }],
    specialFields: [{ key: randomId() }],
    startRevisionsInProgress: false,
    documentDeliveryMode: 'unsigned',
  },
  onSubmit = noop,
  remoteSourceId,
}: ChastiaConfigFormProps) {
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const form = useForm<ChastiaConfig>({
    initialValues: normalizeChastiaConfig(initialValues),
  });
  const { openCopyConfigModal } = useCopyRemoteSourceConfig();

  const handleConfigCopy = useCallback(
    (config: ChastiaConfig) => {
      form.setValues(normalizeChastiaConfig(config));
      showNotification({
        title: 'Konfigurácia bola skopírovaná',
        message: 'Konfigurácia z vybraného externého zdroja bola aplikovaná. Nezabudnite zmeny uložiť.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });
    },
    [form]
  );

  const handleCopyButtonClick = useCallback(() => {
    if (remoteSourceId) {
      openCopyConfigModal({
        currentRemoteSourceId: remoteSourceId,
        onConfigSelected: handleConfigCopy,
      });
    }
  }, [openCopyConfigModal, remoteSourceId, handleConfigCopy]);

  const copyButton = remoteSourceId ? (
    <Button variant="secondary" leftIcon={<IconCopy size={20} />} onClick={handleCopyButtonClick}>
      Kopírovať konfiguráciu
    </Button>
  ) : undefined;

  return (
    <FormWrapper
      onSubmit={form.onSubmit((values) => submit(normalizeChastiaConfig(values)))}
      loading={loading}
      primaryButtonText="Uložiť"
      primaryIcon={<IconCheck stroke="1.5" height={24} width={24} />}
      skipSecondaryButtonConfirm={!form.isDirty()}
      footerRightSide={copyButton}
    >
      <WorkflowSettings form={form} />
      <ISObjects form={form} />
      <DeviceTypes form={form} />
      <SpecialFields form={form} />
      <Technicians form={form} />
    </FormWrapper>
  );
}
